import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { usePathname } from "@/i18n/navigation";
import { useTranslations } from "next-intl";

const emptySubscribe = () => () => {};
const getClientSnapshot = () => true;
const getServerSnapshot = () => false;

export function useHeaderViewModel() {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const t = useTranslations("header");
  const tNav = useTranslations("nav");
  const tSite = useTranslations("site");
  const containerRef = useRef<HTMLUListElement>(null);

  const mounted = useSyncExternalStore(
    emptySubscribe,
    getClientSnapshot,
    getServerSnapshot
  );

  const [indicatorStyle, setIndicatorStyle] = useState({
    left: 0,
    width: 0,
    total: 0,
    opacity: 0,
  });

  /* Optimistic aktif tab: tıklanır tıklanmaz gösterge yeni taba kayar,
     sayfanın yüklenmesi beklenmez. Pathname değişince temizlenir. */
  const [pendingHref, setPendingHref] = useState<string | null>(null);

  const [lastPathname, setLastPathname] = useState(pathname);
  if (lastPathname !== pathname) {
    setLastPathname(pathname);
    setOpen(false);
    setPendingHref(null);
  }

  const isActive = (href: string) => {
    if (pendingHref !== null) return pendingHref === href;
    return href === "/" ? pathname === "/" : pathname.startsWith(href);
  };

  const onNavClick = (href: string) => setPendingHref(href);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /* Mobil menü açıkken <html>'e attribute koyulur; CSS bu attribute ile
     sayfayı sola iterek native uygulama hissi verir (push drawer). */
  useEffect(() => {
    const el = document.documentElement;
    if (open) el.setAttribute("data-menu-open", "");
    else el.removeAttribute("data-menu-open");
    return () => el.removeAttribute("data-menu-open");
  }, [open]);

  useEffect(() => {
    if (!mounted) return;
    const update = () => {
      const container = containerRef.current;
      if (!container) return;
      const activeEl = container.querySelector('[aria-current="page"]');
      if (activeEl) {
        const activeRect = activeEl.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        setIndicatorStyle({
          left: activeRect.left - containerRect.left + container.scrollLeft,
          width: activeRect.width,
          total: container.scrollWidth,
          opacity: 1,
        });
      } else {
        setIndicatorStyle((prev) => ({ ...prev, opacity: 0 }));
      }
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [pathname, pendingHref, mounted]);

  return {
    open,
    setOpen,
    t,
    tNav,
    tSite,
    isActive,
    onNavClick,
    containerRef,
    indicatorStyle,
    mounted,
  };
}
